/**
 * Eigenbau: Kennzahlband über einer Tintenlinie — 18 Stellen im Entwurf.
 *
 * Die Bauform, mit der jeder Bereich des Entwurfs beginnt: eine **volle
 * Tintenlinie** über die Breite, darunter links die Leitzahl groß und dünn,
 * rechts die Nebenwerte klein und mono, auf der Grundlinie ausgerichtet.
 *
 * Die Linie ist `--tinte`, nicht `--linie`. Das ist Absicht und der Unterschied
 * zu jeder anderen Trennung im Entwurf: Hier beginnt ein Abschnitt, dort endet
 * nur eine Zeile. Eine Haarlinie in Linienfarbe würde das Band als Tabellenkopf
 * lesen lassen.
 *
 * `items-end` statt `items-baseline`: Die Leitzahl ist 62px, die Nebenwerte
 * 12.5px. Auf einer gemeinsamen Grundlinie würden die kleinen Werte an der
 * Unterkante der großen Ziffern kleben; der Entwurf gibt ihnen deshalb
 * `padding-bottom: 8px` und richtet an der Unterkante aus.
 */

import type { ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { cx, focusRing } from "@/lib/utils";
import { Mikrolabel } from "@/components/entwurf/Mikrolabel";

/**
 * Ein Nebenwert: Zahl in Tinte, Beschriftung leise daneben.
 *
 * Mit `zu` wird der ganze Wert samt Beschriftung zum Verweis. Vorher war das
 * Band eine Sackgasse — es benannte die Arbeit und bot keinen Weg zu ihr,
 * obwohl die gefilterten Adressen längst existierten. Ohne `zu` bleibt alles
 * wie zuvor: Eine Zahl, die nirgends hinführt, soll auch nicht so aussehen.
 *
 * ── Warum die Unterstreichung schon in Ruhe steht ─────────────────────────
 *
 * Sie kam vorher erst beim Überfahren. Am laufenden Bild gemessen war ein
 * verlinkter Nebenwert damit von unverlinktem Text **nicht zu unterscheiden**:
 * dieselbe Tinte, `text-decoration: none`. Wer keinen Zeiger hat, findet so
 * nichts — auf einem Tastfeld gibt es kein Überfahren, und die vier Nebenwerte
 * waren dort schlicht unentdeckbar.
 *
 * Die Ruhefassung ist deshalb `--linie`: dieselbe Haarlinie, mit der der
 * Entwurf ohnehin jede Zeile unterlegt, also leise genug für ein Band aus
 * Zahlen. Beim Überfahren wechselt nur ihre Farbe auf den Akzent — die Form
 * bleibt, es springt nichts. Damit gilt im ganzen Band dieselbe Regel wie in
 * `Verweis`: **unterstrichenes Mono führt woandershin.**
 */
export function Nebenwert({
  wert,
  label,
  zu,
}: {
  wert: number | string;
  label: string;
  zu?: Record<string, string> | undefined;
}) {
  const inhalt = (
    <Mikrolabel grad={12.5} ton="mittel">
      <span className="tabular-nums text-tinte">{wert}</span> {label}
    </Mikrolabel>
  );

  if (!zu) return inhalt;

  return (
    <Link
      to="/aufgaben"
      search={zu}
      className={cx(
        "underline decoration-linie decoration-1 underline-offset-[3px]",
        "transition-colors duration-150 ease-aus hover:decoration-akzent",
        focusRing,
      )}
    >
      {inhalt}
    </Link>
  );
}

export function Zahlenband({
  children,
  neben,
  className,
}: {
  /** Links: die Leitzahl, üblicherweise eine `Kennzahl`. */
  children: ReactNode;
  /** Rechts: die Nebenwerte, üblicherweise mehrere `Nebenwert`. */
  neben?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cx(
        "flex flex-wrap items-end gap-x-11 gap-y-6 border-t border-tinte pt-[22px]",
        className,
      )}
    >
      {children}
      {neben ? (
        <div className="flex min-w-[260px] flex-1 flex-wrap gap-x-[22px] gap-y-1.5 pb-2">
          {neben}
        </div>
      ) : null}
    </div>
  );
}
